import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Cookie, Settings, Shield, Info } from 'lucide-react'
import SEO, { schemas } from '../components/SEO'
import { useLocale } from '../i18n/Link'

const PRIVACY_EMAIL = 'privacy@cmdesk.help'
const UPDATED_AT = new Date(2026, 2, 28)

// Same order as `types` in the locale file: only the first type is required.
const cookieTypeRequired = [true, false, false, false]

interface CookieType {
  name: string
  description: string
  examples: string[]
}

export default function Cookies() {
  const { t } = useTranslation(['cookies', 'common'])
  const { htmlLang } = useLocale()
  const updatedAt = UPDATED_AT.toLocaleDateString(htmlLang, { day: 'numeric', month: 'long', year: 'numeric' })
  const cookieTypes = (t('types', { returnObjects: true }) as CookieType[]).map((type, i) => ({
    ...type,
    required: cookieTypeRequired[i],
  }))

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/cookies"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.cookies'), url: '/cookies' }])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Cookie className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-gray-400 text-lg">
            {t('hero.updated', { date: updatedAt })}
          </p>
        </motion.div>

        <div className="space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Info className="w-6 h-6 text-primary" />
              {t('what.title')}
            </h2>
            <p className="text-gray-400 leading-relaxed">
              {t('what.text')}
            </p>
          </motion.div>

          {cookieTypes.map((type, index) => (
            <motion.div
              key={type.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl p-8"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">{type.name}</h2>
                {type.required ? (
                  <span className="px-3 py-1 bg-primary/20 text-primary text-sm rounded-full">
                    {t('required')}
                  </span>
                ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                )}
              </div>
              <p className="text-gray-400 mb-4">{type.description}</p>
              <div className="flex flex-wrap gap-2">
                {type.examples.map((example) => (
                  <span key={example} className="px-3 py-1 bg-white/5 text-gray-400 text-sm rounded">
                    {example}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Settings className="w-6 h-6 text-secondary" />
              {t('manage.title')}
            </h2>
            <p className="text-gray-400 leading-relaxed mb-4">
              {t('manage.text')}
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#" className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm">Chrome</a>
              <a href="#" className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm">Firefox</a>
              <a href="#" className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm">Safari</a>
              <a href="#" className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm">Edge</a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-accent" />
              {t('rights.title')}
            </h2>
            <p className="text-gray-400 leading-relaxed">
              {t('rights.text', { email: PRIVACY_EMAIL })}
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  )
}
